import { createEffect, createMemo, createSignal, For, JSX, onMount, Show } from 'solid-js'
import { characterStore, chatStore, settingStore, userStore } from '/web/store'
import { elapsedSince, getAssetUrl } from '/web/shared/util'
import { A, useNavigate, useParams } from '@solidjs/router'
import Fuse from 'fuse.js'
import { RotateCcw, Trash } from 'lucide-solid'
import { getOrCreateChatWithCharacter } from '/web/shared/aifans/lib/chat-util'
import { isLoggedIn } from '/web/store/api'

const RecentChatsSearch = (props: any) => {
  return (
    <div class="relative mt-3">
      <form
        action="/search"
        method="get"
        id="search-form"
        onSubmit={(e) => {
          e.preventDefault()
        }}
      >
        <svg
          class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2"
          style={{ color: 'rgba(255,255,255,0.4)' }}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
        <input
          type="text"
          name="query"
          class="h-[40px] w-full rounded-full border border-[var(--cf-hairline-strong)] bg-[var(--cf-bg)] pl-9 pr-4 font-nunito text-[14px] text-white placeholder-[rgba(255,255,255,0.35)] outline-none transition-colors focus:border-[var(--cf-cyan)]"
          placeholder="Search conversations..."
          onInput={props.onChange}
        />
      </form>
    </div>
  )
}

const RecentChatItem = (props: {
  avatar?: string
  title?: string
  subtitle?: string
  description?: string
  selected?: boolean
  id?: string
}) => {
  return (
    <div>
      <A href={`/live-chat/${props.id}`} onClick={() => settingStore.leftPanelMobile(false)}>
        <ChatListItem {...props} showActions={true} />
      </A>
    </div>
  )
}

const NewChatItem = (props: {
  avatar?: string
  title?: string
  subtitle?: string
  description?: string
  selected?: boolean
  id?: string
  onClick: (characterId: string) => void
}) => {
  return (
    <div class="cursor-pointer" onClick={() => props.id && props.onClick(props.id)}>
      <ChatListItem {...props} />
    </div>
  )
}

const ChatListItem = (props: {
  avatar?: string
  title?: string
  subtitle?: string
  description?: string
  selected?: boolean
  showActions?: boolean
}) => {
  return (
    <div
      class="flex items-center gap-3 transition-colors"
      style={{
        padding: props.selected ? '14px 18px 14px 15px' : '14px 18px',
        'border-bottom': '1px solid var(--cf-hairline)',
        cursor: 'pointer',
        background: props.selected ? 'rgba(138,43,226,0.1)' : 'transparent',
        'border-left': props.selected ? '3px solid var(--cf-primary)' : '3px solid transparent',
      }}
      onMouseEnter={(e) => {
        if (!props.selected) e.currentTarget.style.background = 'rgba(255,255,255,0.03)'
      }}
      onMouseLeave={(e) => {
        if (!props.selected) e.currentTarget.style.background = 'transparent'
      }}
    >
      {/* Round avatar */}
      {props.avatar && (
        <img
          class="h-[44px] w-[44px] flex-shrink-0 rounded-full object-cover"
          src={getAssetUrl(props.avatar, true, { width: 88 })}
          alt={props.title}
        />
      )}

      {/* Name + preview */}
      <div class="min-w-0 flex-1">
        <div class="truncate font-rajdhani text-[14px] font-bold text-white">{props.title}</div>
        <div class="mt-0.5 truncate text-[12px] text-[var(--cf-muted)]">{props.description}</div>
      </div>

      {/* Online dot */}
      <div
        class="h-[10px] w-[10px] flex-shrink-0 rounded-full"
        style={{ background: '#2dc653', 'box-shadow': '0 0 6px rgba(45,198,83,0.6)' }}
      />

      {/* Actions — only on selected/active row */}
      <Show when={props.showActions && props.selected}>
        <div class="flex gap-2">
          <button
            class="text-[var(--cf-muted)] transition-colors hover:text-white"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); chatStore.option({ options: false, modal: 'restart' }) }}
          >
            <RotateCcw class="h-3.5 w-3.5" />
          </button>
          <button
            class="text-[var(--cf-muted)] transition-colors hover:text-white"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); chatStore.option({ options: false, modal: 'delete' }) }}
          >
            <Trash class="h-3.5 w-3.5" />
          </button>
        </div>
      </Show>
    </div>
  )
}

const ChatRecentChatsNav = (props: any) => {
  let fuse: any
  const [searchResults, setSearchResults] = createSignal<any[]>([])

  const nav = useNavigate()
  const params = useParams()

  const charState = characterStore((s) => ({ list: s.characters.list }))

  const trendingChars = createMemo(() =>
    charState.list.filter((c: any) => c.category === 'trending').slice(0, 8)
  )

  onMount(() => {
    characterStore.getCharacters()
  })

  const chats = chatStore((s) => ({
    ready: s.allChars.list.length > 0 && (s.active?.char?._id || 'no-id') in s.allChars.map,
    allChars: s.allChars?.list.filter((char) => {
      const chatExists = s.allChats?.find((chat) => char._id === chat.characterId)
      return !chatExists
    }),
    allChats: s.allChats,
    lastChatId: s.lastChatId,
    recentChats: s.allChats.map((chat) => {
      const character = s.allChars?.list?.find((char) => char._id === chat.characterId)

      return {
        ...chat,
        character: { ...character },
      }
    }),
    favoriteCharacters: s.allChars?.list
      .filter((char) => {
        return userStore().profile?.likedCharacters?.includes(char._id)
      })
      .sort((a, b) => a.name.localeCompare(b.name)),
  }))

  createEffect(() => {
    if (chats?.recentChats) {
      fuse = new Fuse(chats.recentChats, {
        keys: ['character.name', 'character.realName'],
        includeMatches: true,
        includeScore: true,
        minMatchCharLength: 2,
      })

      setSearchResults(chats.recentChats)
    }
  })

  const onChange: JSX.EventHandler<HTMLFormElement, InputEvent> = (e) => {
    const results = fuse.search(e.currentTarget.value)

    if (results.length) {
      setSearchResults(results.map((i: any) => i.item))
    } else {
      setSearchResults(chats?.recentChats)
    }
  }

  const createChat = (characterId: string, presetId?: string) => {
    getOrCreateChatWithCharacter(characterId, (url) => {
      nav(url)
      settingStore.leftPanelMobile(false)
    })
  }

  return (
    <div
      class="flex flex-col overflow-y-auto md:w-[280px]"
      style={{
        background: 'var(--cf-surface)',
        'border-right': '1px solid var(--cf-hairline)',
        'scrollbar-width': 'thin',
      }}
    >
      <Show when={chats?.recentChats.length > 0}>
        <div class="px-[18px] py-[18px]" style={{ 'border-bottom': '1px solid var(--cf-hairline)' }}>
          <h2 class="font-bebas text-[22px] tracking-[1px] text-white">Conversations</h2>
        </div>
        <div class="flex flex-col">
          {chats.recentChats?.map((chat: any) => {
            return (
              <RecentChatItem
                title={chat.character?.name}
                subtitle={chat.character?.realName}
                description={`${elapsedSince(chat.updatedAt)} ago`}
                avatar={chat.character?.avatar}
                selected={chats.lastChatId === chat._id || params.id === chat._id}
                id={chat._id}
              />
            )
          })}
        </div>
      </Show>
      <Show when={chats?.favoriteCharacters.length > 0}>
        <div class="px-[18px] py-[18px]" style={{ 'border-bottom': '1px solid var(--cf-hairline)' }}>
          <h2 class="font-bebas text-[22px] tracking-[1px] text-white">Favorites</h2>
        </div>
        <div class="flex flex-col">
          {chats?.favoriteCharacters?.map((character: any) => {
            // check if a chat exists
            const chat = chats?.allChats?.find((chat) => character._id === chat.characterId)
            if (chat) {
              return (
                <RecentChatItem
                  title={character?.name}
                  subtitle={character?.realName}
                  description={`${elapsedSince(chat.updatedAt)} ago`}
                  avatar={character?.avatar}
                  selected={chats.lastChatId === chat._id}
                  id={chat._id}
                />
              )
            } else {
              return (
                <NewChatItem
                  title={character?.name}
                  subtitle={character?.realName}
                  description={`Start a new chat`}
                  avatar={character?.avatar}
                  selected={chats.lastChatId === character?._id}
                  id={character?._id}
                  onClick={(characterId) => createChat(characterId)}
                />
              )
            }
          })}
        </div>
      </Show>

      {/* Default characters — shown when no chat history (also the only thing logged-out users see) */}
      <Show when={chats.recentChats.length === 0 && chats.favoriteCharacters.length === 0 && trendingChars().length > 0}>
        <div class="px-[18px] py-[18px]" style={{ 'border-bottom': '1px solid var(--cf-hairline)' }}>
          <h2 class="font-bebas text-[22px] tracking-[1px] text-white">Start a conversation</h2>
        </div>
        <div class="flex flex-col">
          <For each={trendingChars()}>
            {(character: any) => (
              <NewChatItem
                title={character.name}
                subtitle={character.realName}
                description="Start chatting"
                avatar={character.avatar}
                id={character._id}
                onClick={(characterId) => {
                  if (!isLoggedIn()) {
                    userStore.loginModal(true)
                  } else {
                    createChat(characterId)
                  }
                }}
              />
            )}
          </For>
        </div>
      </Show>
    </div>
  )
}

export default ChatRecentChatsNav
